import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut, RotateCw, Check, X } from "lucide-react";

/**
 * Reads back a cropped square data URL from the original image.
 * area is { x, y, width, height } in image pixel coords.
 */
async function getCroppedDataUrl(imageSrc, area, rotation = 0) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const size = Math.min(1024, Math.max(area.width, area.height));
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  // White background so transparent PNGs still look clean in invoice PDFs.
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, size, size);
  if (rotation) {
    ctx.translate(size / 2, size / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-size / 2, -size / 2);
  }
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return canvas.toDataURL("image/png", 0.95);
}

/**
 * Modal for cropping/zooming/rotating a freshly-uploaded logo into a clean
 * square. Calls onConfirm(dataUrl) once the user is happy.
 */
export default function LogoCropDialog({ open, imageSrc, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, areaPx) => {
    setCroppedAreaPixels(areaPx);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const out = await getCroppedDataUrl(imageSrc, croppedAreaPixels, rotation);
      onConfirm(out);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="logo-crop-dialog">
        <DialogHeader>
          <DialogTitle>Crop your logo</DialogTitle>
          <DialogDescription>
            Drag to reposition, pinch or use the slider to zoom. Logos look best when centered in a square.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full h-72 bg-slate-900 rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              style={{ containerStyle: { background: "#0f172a" } }}
            />
          )}
        </div>

        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-slate-400" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.05}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="logo-zoom-slider"
              className="flex-1"
            />
            <ZoomIn className="w-4 h-4 text-slate-400" />
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Zoom {zoom.toFixed(2)}×</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                data-testid="logo-rotate-btn"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1" /> Rotate
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="logo-reset-btn">
                Reset
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel} data-testid="logo-crop-cancel">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="logo-crop-confirm"
          >
            <Check className="w-4 h-4 mr-1" />
            {busy ? "Cropping…" : "Apply crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
